// SimulatorHistory.js — Tab Histórico no simulador
// Mostra histórico de simulações com filtros (cliente(s), candidato, intervalo de datas)
// + gráfico evolutivo (1 cliente selecionado) ou comparativo (>=2 clientes).
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { History, Loader2, Search, Calendar, ExternalLink, TrendingUp, X, Check, Trash2, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) =>
  v != null
    ? new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(v)
    : "—";
const fmtPct = (v) => (v != null ? `${Number(v).toFixed(1)}%` : "—");
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit", year: "2-digit" });
  } catch {
    return iso;
  }
};

const REGIME_LABELS = { contract: "Contrato", green_receipts: "Recibos Verdes", b2b: "B2B" };
const BIZ_LABELS = { outsourcing: "Outsourcing", perm: "Perm" };

// Calcula date_from/date_to ISO (yyyy-mm-dd) com base num preset
function rangeToDates(preset, customFrom, customTo) {
  if (preset === "all") return { date_from: undefined, date_to: undefined };
  const now = new Date();
  const toIso = (d) => d.toISOString().slice(0, 10);
  if (preset === "30d") {
    const f = new Date(now);
    f.setDate(now.getDate() - 30);
    return { date_from: toIso(f), date_to: toIso(now) };
  }
  if (preset === "90d") {
    const f = new Date(now);
    f.setDate(now.getDate() - 90);
    return { date_from: toIso(f), date_to: toIso(now) };
  }
  if (preset === "ytd") {
    return { date_from: `${now.getFullYear()}-01-01`, date_to: toIso(now) };
  }
  if (preset === "custom") {
    return {
      date_from: customFrom || undefined,
      date_to: customTo || undefined,
    };
  }
  return {};
}

const SimulatorHistory = () => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [clientIds, setClientIds] = useState([]); // multi-select
  const [candidate, setCandidate] = useState("");
  const [preset, setPreset] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [data, setData] = useState({ items: [], stats: { simulations_count: 0, avg_margin_percentage: 0 } });

  const fetchClients = async () => {
    try {
      const r = await axios.get(`${API}/clients`, { headers: getAuthHeaders() });
      setClients(r.data || []);
    } catch {
      /* silent */
    }
  };

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const { date_from, date_to } = rangeToDates(preset, customFrom, customTo);
      const params = {};
      if (clientIds.length > 0) params.client_ids = clientIds.join(",");
      if (candidate.trim()) params.candidate = candidate.trim();
      if (date_from) params.date_from = date_from;
      if (date_to) params.date_to = date_to;
      const r = await axios.get(`${API}/history`, { headers: getAuthHeaders(), params });
      setData(r.data);
    } catch {
      toast.error("Erro a carregar histórico");
    } finally {
      setLoading(false);
    }
  };

  const [cleaning, setCleaning] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const cleanupDuplicates = async () => {
    if (!window.confirm("Tem a certeza que quer limpar simulações duplicadas? Serão eliminadas todas as simulações repetidas mantendo apenas a mais recente de cada.")) return;
    setCleaning(true);
    try {
      const r = await axios.post(`${API}/simulations/cleanup-duplicates`, {}, { headers: getAuthHeaders() });
      const n = r?.data?.deleted_count || 0;
      if (n === 0) toast.info("Não foram encontrados duplicados.");
      else toast.success(`${n} simulação(ões) duplicada(s) eliminada(s).`);
      await fetchHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao limpar duplicados");
    } finally {
      setCleaning(false);
    }
  };

  const deleteRow = async (row) => {
    if (!row?.id) return;
    const label = row.candidate_name ? `de ${row.candidate_name}` : "seleccionada";
    if (!window.confirm(`Eliminar a simulação ${label} de ${new Date(row.updated_at).toLocaleDateString("pt-PT")} ?`)) return;
    setDeletingId(row.id);
    try {
      await axios.delete(`${API}/simulations/${row.id}`, { headers: getAuthHeaders() });
      toast.success("Simulação eliminada");
      await fetchHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao eliminar simulação");
    } finally {
      setDeletingId(null);
    }
  };

  // Multi-selecção — permite eliminar várias simulações duma só vez
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const toggleSelect = (id) => setSelectedIds(prev => {
    const s = new Set(prev);
    if (s.has(id)) s.delete(id); else s.add(id);
    return s;
  });
  const toggleSelectAll = (visible) => setSelectedIds(prev => {
    if (visible.every(id => prev.has(id)) && visible.length > 0) return new Set();
    return new Set(visible);
  });
  const clearSelection = () => setSelectedIds(new Set());
  const deleteSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Eliminar ${selectedIds.size} simulação(ões) seleccionada(s)? Esta acção não pode ser revertida.`)) return;
    setBulkDeleting(true);
    let ok = 0, fail = 0;
    for (const id of Array.from(selectedIds)) {
      try {
        await axios.delete(`${API}/simulations/${id}`, { headers: getAuthHeaders() });
        ok++;
      } catch { fail++; }
    }
    setBulkDeleting(false);
    clearSelection();
    if (ok > 0) toast.success(`${ok} simulação(ões) eliminada(s)${fail > 0 ? ` (${fail} falharam)` : ''}.`);
    if (ok === 0 && fail > 0) toast.error(`Nenhuma foi eliminada (${fail} erros).`);
    await fetchHistory();
  };

  useEffect(() => {
    fetchClients();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounce de fetch quando filtros mudam
  useEffect(() => {
    const t = setTimeout(fetchHistory, 250);
    return () => clearTimeout(t);
  }, [clientIds, candidate, preset, customFrom, customTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleClient = (id) => {
    setClientIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const selectedClients = clients.filter((c) => clientIds.includes(c.id));

  const items = data.items || [];
  const stats = data.stats || { simulations_count: 0, avg_margin_percentage: 0 };

  const totalMargin = useMemo(
    () => items.reduce((acc, it) => acc + (Number(it.margin_amount) || 0), 0),
    [items]
  );

  return (
    <div className="space-y-4" data-testid="simulator-history-tab">
      {/* Filtros */}
      <Card className="bg-white">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2 text-brand-600">
            <History className="w-4 h-4" /> Histórico de Simulações
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-12 gap-2">
          <div className="md:col-span-4">
            <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Clientes (multi)</div>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-between font-normal bg-white"
                  data-testid="history-client-filter"
                >
                  <span className="truncate text-left">
                    {clientIds.length === 0
                      ? "Todos os clientes"
                      : `${clientIds.length} cliente${clientIds.length > 1 ? "s" : ""} selecionado${clientIds.length > 1 ? "s" : ""}`}
                  </span>
                  <span className="text-slate-400 text-xs">▾</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0 max-h-80 overflow-y-auto" align="start">
                <div className="p-2 border-b border-slate-100 flex items-center justify-between">
                  <span className="text-[11px] text-slate-500 font-semibold">
                    Selecione 1+ clientes
                  </span>
                  {clientIds.length > 0 && (
                    <button
                      onClick={() => setClientIds([])}
                      className="text-[11px] text-brand-600 hover:underline"
                      data-testid="clear-client-filter"
                    >
                      Limpar
                    </button>
                  )}
                </div>
                <ul className="py-1">
                  {clients.length === 0 ? (
                    <li className="text-xs text-slate-400 px-3 py-2">Sem clientes</li>
                  ) : (
                    clients.map((c) => {
                      const checked = clientIds.includes(c.id);
                      return (
                        <li key={c.id}>
                          <button
                            onClick={() => toggleClient(c.id)}
                            className={`flex items-center w-full px-3 py-1.5 text-left text-xs hover:bg-slate-50 ${checked ? "bg-brand-50" : ""}`}
                            data-testid={`client-option-${c.id}`}
                          >
                            <span className={`w-4 h-4 mr-2 flex-shrink-0 rounded border ${checked ? "bg-brand-600 border-brand-600 flex items-center justify-center" : "border-slate-300"}`}>
                              {checked && <Check className="w-3 h-3 text-white" />}
                            </span>
                            <span className="flex-1 truncate">{c.name}</span>
                            <span className="text-[10px] text-slate-400 ml-2 font-mono">
                              {c.payment_terms}d · {c.billable_months}m
                            </span>
                          </button>
                        </li>
                      );
                    })
                  )}
                </ul>
              </PopoverContent>
            </Popover>
            {/* Badges com cliente + prazo */}
            {selectedClients.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5" data-testid="selected-clients-badges">
                {selectedClients.map((c) => (
                  <Badge
                    key={c.id}
                    variant="outline"
                    className="text-[10px] border-brand-600 bg-brand-50 text-brand-700 gap-1 pr-1"
                  >
                    <span>{c.name}</span>
                    <span className="text-[9px] text-slate-500 font-mono">· {c.payment_terms}d</span>
                    <button
                      onClick={() => toggleClient(c.id)}
                      className="ml-0.5 hover:text-red-600"
                      title="Remover"
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <div className="md:col-span-4">
            <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Candidato (nome / NIF / email)</div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={candidate}
                onChange={(e) => setCandidate(e.target.value)}
                placeholder="Procurar candidato..."
                className="pl-9"
                data-testid="history-candidate-filter"
              />
            </div>
          </div>
          <div className="md:col-span-4">
            <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Intervalo</div>
            <Select value={preset} onValueChange={setPreset}>
              <SelectTrigger className="bg-white" data-testid="history-range-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todo o histórico</SelectItem>
                <SelectItem value="30d">Últimos 30 dias</SelectItem>
                <SelectItem value="90d">Últimos 90 dias</SelectItem>
                <SelectItem value="ytd">Ano corrente</SelectItem>
                <SelectItem value="custom">Personalizado…</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {preset === "custom" && (
            <div className="md:col-span-12 grid grid-cols-2 gap-2 mt-1">
              <div>
                <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1">De</div>
                <Input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  data-testid="history-date-from"
                />
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Até</div>
                <Input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  data-testid="history-date-to"
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="bg-white">
          <CardContent className="p-3">
            <div className="text-[10px] uppercase text-slate-500 font-semibold">Simulações</div>
            <div className="text-xl font-bold text-brand-600 mt-1">{stats.simulations_count}</div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-3">
            <div className="text-[10px] uppercase text-slate-500 font-semibold flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> Margem média
            </div>
            <div className="text-xl font-bold text-brand-600 mt-1">{fmtPct(stats.avg_margin_percentage)}</div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-3">
            <div className="text-[10px] uppercase text-slate-500 font-semibold">Margem total (mensal)</div>
            <div className="text-xl font-bold text-brand-600 mt-1">{fmt(totalMargin)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos: evolutivo (1 cliente) ou comparativo (>=2 clientes) */}
      {clientIds.length === 1 && items.length > 0 && (() => {
        const evol = [...items].reverse().map((it) => ({
          date: it.updated_at ? new Date(it.updated_at).toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit" }) : "—",
          candidate: it.candidate_name,
          margin_pct: Number(it.margin_percentage) || 0,
        }));
        return (
          <Card className="bg-white" data-testid="evolution-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-brand-600">
                <TrendingUp className="w-4 h-4" /> Evolução da Margem (%) — {selectedClients[0]?.name}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={evol} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" stroke="#64748b" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#64748b" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                    formatter={(v) => [`${Number(v).toFixed(1)}%`, "Margem"]}
                    labelFormatter={(label, payload) => {
                      const p = payload && payload[0];
                      return p?.payload?.candidate ? `${label} · ${p.payload.candidate}` : label;
                    }}
                  />
                  <Line type="monotone" dataKey="margin_pct" stroke="var(--brand-primary)" strokeWidth={2} dot={{ r: 4, fill: "var(--brand-primary)" }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        );
      })()}

      {clientIds.length >= 2 && (() => {
        // Agregação por cliente
        const aggMap = {};
        items.forEach((it) => {
          const cid = it.client_id || "__none__";
          if (!aggMap[cid]) aggMap[cid] = { count: 0, marginPctSum: 0, rateHourlySum: 0, marginAmountSum: 0 };
          aggMap[cid].count += 1;
          aggMap[cid].marginPctSum += Number(it.margin_percentage) || 0;
          aggMap[cid].rateHourlySum += Number(it.rate_hourly) || 0;
          aggMap[cid].marginAmountSum += Number(it.margin_amount) || 0;
        });
        const compare = selectedClients.map((c) => {
          const a = aggMap[c.id] || { count: 0, marginPctSum: 0, rateHourlySum: 0, marginAmountSum: 0 };
          return {
            id: c.id,
            name: c.name.length > 14 ? c.name.slice(0, 14) + "…" : c.name,
            fullName: c.name,
            margin_pct_avg: a.count ? +(a.marginPctSum / a.count).toFixed(2) : 0,
            rate_hourly_avg: a.count ? +(a.rateHourlySum / a.count).toFixed(2) : 0,
            margin_total: +a.marginAmountSum.toFixed(2),
            simulations: a.count,
          };
        });
        // Calcular ranking por margem média (desc)
        const ranking = [...compare]
          .map((c) => c.id)
          .sort((aId, bId) => {
            const av = compare.find((c) => c.id === aId)?.margin_pct_avg || 0;
            const bv = compare.find((c) => c.id === bId)?.margin_pct_avg || 0;
            return bv - av;
          });
        const rankMap = Object.fromEntries(ranking.map((id, idx) => [id, idx + 1]));
        return (
          <Card className="bg-white" data-testid="comparison-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-brand-600">
                <TrendingUp className="w-4 h-4" /> Comparativo entre Clientes ({selectedClients.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Ranking por margem média */}
              <div className="flex flex-wrap gap-2 mb-3" data-testid="ranking-list">
                {ranking.slice(0, 3).map((id, idx) => {
                  const c = compare.find((x) => x.id === id);
                  if (!c) return null;
                  const colors = [
                    "bg-amber-50 border-amber-300 text-amber-900",
                    "bg-slate-50 border-slate-300 text-slate-700",
                    "bg-orange-50 border-orange-300 text-orange-800",
                  ];
                  return (
                    <div
                      key={id}
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md border ${colors[idx]}`}
                      data-testid={`rank-${idx + 1}`}
                    >
                      <span className="text-[11px] font-bold">#{idx + 1}</span>
                      <span className="text-xs font-semibold truncate max-w-[160px]">{c.fullName}</span>
                      <span className="text-[10px] font-mono">{c.margin_pct_avg.toFixed(1)}%</span>
                    </div>
                  );
                })}
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={compare} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="name"
                    stroke="#64748b"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(name, idx) => {
                      const item = compare[idx];
                      if (!item) return name;
                      const r = rankMap[item.id];
                      return r ? `#${r} ${name}` : name;
                    }}
                  />
                  <YAxis
                    yAxisId="left"
                    stroke="var(--brand-primary)"
                    tick={{ fontSize: 10 }}
                    tickFormatter={(v) => `${v}%`}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    stroke="#64748b"
                    tick={{ fontSize: 10 }}
                    tickFormatter={(v) => `${v}€`}
                  />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                    formatter={(v, name) => {
                      if (name === "margin_pct_avg") return [`${v}%`, "Margem média"];
                      if (name === "rate_hourly_avg") return [`${v}€`, "Rate/Hora médio"];
                      return [v, name];
                    }}
                    labelFormatter={(label, payload) => {
                      const p = payload && payload[0];
                      return p?.payload?.fullName ? `${p.payload.fullName} · ${p.payload.simulations} sim.` : label;
                    }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 11 }}
                    formatter={(v) =>
                      v === "margin_pct_avg" ? "Margem média (%)" : v === "rate_hourly_avg" ? "Rate/Hora médio (€)" : v
                    }
                  />
                  <Bar yAxisId="left" dataKey="margin_pct_avg" fill="var(--brand-primary)" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="right" dataKey="rate_hourly_avg" fill="#7BAE9F" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        );
      })()}

      {/* Tabela */}
      <Card className="bg-white">
        <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2">
          <CardTitle className="text-sm flex items-center gap-2 text-brand-600">
            <Calendar className="w-4 h-4" /> Resultados ({items.length})
            {selectedIds.size > 0 && (
              <span className="ml-2 text-xs text-slate-600 font-normal" data-testid="selection-count">
                · {selectedIds.size} seleccionada(s)
              </span>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            {selectedIds.size > 0 && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={clearSelection}
                  className="h-7 text-xs"
                  data-testid="clear-selection-btn"
                >
                  Limpar selecção
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={deleteSelected}
                  disabled={bulkDeleting}
                  className="h-7 text-xs text-red-600 border-red-300 hover:bg-red-50"
                  data-testid="delete-selected-btn"
                >
                  {bulkDeleting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
                  Eliminar {selectedIds.size}
                </Button>
              </>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={cleanupDuplicates}
              disabled={cleaning || items.length === 0}
              className="h-7 text-xs"
              data-testid="cleanup-duplicates-btn"
              title="Elimina simulações repetidas mantendo apenas a mais recente"
            >
              {cleaning ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
              Limpar Duplicados
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-brand-600" />
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">Sem resultados para os filtros aplicados.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="history-table">
                <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                  <tr>
                    <th className="p-2 w-8">
                      <input
                        type="checkbox"
                        className="cursor-pointer accent-brand-600"
                        checked={items.length > 0 && items.every(it => selectedIds.has(it.id))}
                        onChange={() => toggleSelectAll(items.map(it => it.id))}
                        data-testid="select-all-checkbox"
                        title="Seleccionar todas nesta lista"
                      />
                    </th>
                    <th className="text-left p-2">Data</th>
                    <th className="text-left p-2">Cliente</th>
                    <th className="text-left p-2">Candidato</th>
                    <th className="text-left p-2">Tipo</th>
                    <th className="text-left p-2">Regime</th>
                    <th className="text-right p-2">Bruto</th>
                    <th className="text-right p-2">Rate Mês</th>
                    <th className="text-right p-2">Rate Hora</th>
                    <th className="text-right p-2">Margem</th>
                    <th className="text-right p-2">Margem %</th>
                    <th className="text-right p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id} className={`border-t border-slate-100 hover:bg-slate-50 ${selectedIds.has(it.id) ? 'bg-brand-50' : ''}`}>
                      <td className="p-2 w-8">
                        <input
                          type="checkbox"
                          className="cursor-pointer accent-brand-600"
                          checked={selectedIds.has(it.id)}
                          onChange={() => toggleSelect(it.id)}
                          data-testid={`select-row-${it.id}`}
                        />
                      </td>
                      <td className="p-2 text-slate-600 whitespace-nowrap">{fmtDate(it.updated_at)}</td>
                      <td className="p-2 text-slate-700">
                        {it.client_name ? (
                          <Link to={`/admin/clients/${it.client_id}`} className="text-brand-600 hover:underline">
                            {it.client_name}
                          </Link>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="p-2 font-medium text-slate-800">{it.candidate_name}</td>
                      <td className="p-2">
                        <Badge variant="outline" className="text-[10px] font-normal">
                          {BIZ_LABELS[it.business_type] || it.business_type || "—"}
                        </Badge>
                      </td>
                      <td className="p-2 text-slate-600">{REGIME_LABELS[it.regime_type] || it.regime_type || "—"}</td>
                      <td className="p-2 text-right font-mono">{fmt(it.gross_value)}</td>
                      <td className="p-2 text-right font-mono">{fmt(it.rate_monthly)}</td>
                      <td className="p-2 text-right font-mono">{fmt(it.rate_hourly)}</td>
                      <td className="p-2 text-right font-mono font-semibold text-brand-600">{fmt(it.margin_amount)}</td>
                      <td className="p-2 text-right font-mono">{fmtPct(it.margin_percentage)}</td>
                      <td className="p-2 text-right">
                        <div className="flex items-center gap-1 justify-end">
                          <Link to={`/simulator/shared/${it.id}`} target="_blank">
                            <Button size="icon" variant="ghost" className="h-7 w-7" title="Abrir simulação">
                              <ExternalLink className="w-3.5 h-3.5" />
                            </Button>
                          </Link>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                            title="Eliminar simulação"
                            onClick={() => deleteRow(it)}
                            disabled={deletingId === it.id}
                            data-testid={`delete-simulation-${it.id}`}
                          >
                            {deletingId === it.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="w-3.5 h-3.5" />
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SimulatorHistory;
